import {
  AddressProps,
  FinanceProfilePaymentFormsEnum,
  FinanceProfileTransactionPaymentMethodsEnum,
  InventoryTransactionProps,
  ProfileProps,
  SupplierForecastProps,
  SupplierOrderCreatorUserProps,
  SupplierProductProps,
  TransportCompanyProps,
  UserProps,
} from "@/types";
import { CatalogProductProps } from "./catalog-product.type";
import { SupplierProps } from "./supplier.type";

export enum QuotationProductPackageEnum {
  "25kg_bags" = "25kg_bags",
  "25kg_boxes" = "25kg_boxes",
  "super_sacks" = "super_sacks",
  "in_bulk" = "in_bulk",
  "gaylord_boxes" = "gaylord_boxes",
  "rolls" = "rolls",
}

export enum QuotationAcceptedOrRejectedFromEnum {
  "whatsapp_assistant" = "whatsapp_assistant",
  "mercai_os_authenticated" = "mercai_os_authenticated",
  "mercai_os_unauthenticated" = "mercai_os_unauthenticated",
  "mercai_os_assistant" = "mercai_os_assistant",
  "opus" = "opus",
}

export interface QuotationNonCatalogProductProps {
  description: string;
  link: string;
}

export interface QuotationProductProps {
  uuid: string;
  quantity: number;
  cost: number;
  price: number;
  supplier: SupplierProps;
  supplierUuid?: string;
  supplierProduct?: SupplierProductProps;
  supplierProductUuid?: string;
  supplierForecastUuid?: string;
  supplierForecast?: SupplierForecastProps;
  supplyInventoryTransactionUuid?: string;
  supplyInventoryTransaction?: InventoryTransactionProps;
  catalogProduct: CatalogProductProps;
  nonCatalogProduct: QuotationNonCatalogProductProps;
  catalogProductUuid: string;
  selected: boolean;
  package: keyof typeof QuotationProductPackageEnum;
  traderCommission: number;
  isQuoted?: boolean;
  createdAt: string;
  observation?: string;
  quotation: QuotationProps; // eslint-disable-line
  product?: {
    title?: string;
    smartCatalogProductUuid?: string;
  };
}

export enum QuotationStatusEnum {
  "created" = "created",
  "pending" = "pending",
  "active" = "active",
  "accepted" = "accepted",
  "expired" = "expired",
  "rejected" = "rejected",
  "cancelled" = "cancelled",
  "pending_approval" = "pending_approval",
  "ready" = "ready",
}

export enum QuotationDeliveryEnum {
  "pick_up" = "pick_up",
  "receive_at_address" = "receive_at_address",
}

export enum QuotationInvoiceOptionEnum {
  "buyer" = "buyer",
  "general_public" = "general_public",
}

export enum QuotationRecurrencyOptionEnum {
  "once" = "once",
  "quarterly" = "quarterly",
}

export interface QuotationAIComplementInsufficientQuantityAlertProps {
  type: "insuficient_quantity_available" | "minimum_purchase_quantity";
  quantityDesired: number;
  quantityAvailable: number;
  minimumPurchaseQuantity: number;
}

export interface QuotationAIComplementDynamicMarginAlertProps {
  type: "dynamic_margins";
  cm3Margin: string;
  description: string;
  explanation: string;
  grossMargin: string;
  accuracyPercentage: string;
}

export interface QuotationAIComplementDynamicCostAlertProps {
  type: "dynamic_cost";
  title: string;
  description: string;
  explanation: string;
  searchDate: string;
  accuracyPercentage: string;
  catalogProductUuid: string;
}

export interface QuotationAIComplementLastMileCostAlertProps {
  type: "last_mile_cost";
  title: string;
  description: string;
  explanation: string;
}

export type QuotationAIComplementAlertProps =
  | QuotationAIComplementInsufficientQuantityAlertProps
  | QuotationAIComplementDynamicMarginAlertProps
  | QuotationAIComplementDynamicCostAlertProps
  | QuotationAIComplementLastMileCostAlertProps;

export type QuotationAIComplementAlertKeyProps =
  QuotationAIComplementAlertProps[];

export type QuotationAIComplementDataReasonFieldsExplanationProps = {
  explanation: string;
};

export interface QuotationAIComplementDataReasonFieldsCostProps {
  cost: string;
  explanation: string;
  accuracyPercentage: string;
}

export interface QuotationAIComplementDataReasonFieldsPriceProps {
  price: string;
  explanation: string;
  accuracyPercentage: string;
}

export interface QuotationAIComplementDataReasonFieldsItemProps {
  catalogProductUuid: string;
  cost: QuotationAIComplementDataReasonFieldsCostProps[];
  price: QuotationAIComplementDataReasonFieldsPriceProps[];
}

export interface QuotationAIComplementDataReasonFieldsVehicleProps {
  cost: number;
  type: string;
  capacityKg: number;
}

export interface QuotationAIComplementDataReasonFieldsReasonProps {
  routeUsed: string;
  distanceKm: number;
  vehiclesSelected: QuotationAIComplementDataReasonFieldsVehicleProps[];
  totalCostForThisShipment: number;
}

export interface QuotationAIComplementDataReasonFieldsDefaultProps {
  explanation: string;
}

export interface QuotationAIComplementDataReasonFieldsFirstMileCostProps {
  reasons: QuotationAIComplementDataReasonFieldsReasonProps[];
  explanation: string;
}

export type QuotationAIComplementDataReasonFieldsProps = {
  items?: QuotationAIComplementDataReasonFieldsItemProps[];
  customsCost?: QuotationAIComplementDataReasonFieldsDefaultProps[];
  lastMileCost?: QuotationAIComplementDataReasonFieldsDefaultProps[];
  firstMileCost?: QuotationAIComplementDataReasonFieldsFirstMileCostProps[];
};

export interface QuotationAIComplementSmartMileVehicleProps {
  cost: number;
  type: string;
  capacityKg: number;
}

export interface QuotationAIComplementSmartMileReasonProps {
  routeUsed: string;
  distanceKm: number;
  vehiclesSelected: QuotationAIComplementSmartMileVehicleProps[];
  totalCostForThisShipment: number;
}

export interface QuotationAIComplementSmartFirstMileProps {
  reasons: QuotationAIComplementSmartMileReasonProps[];
  capacity: number;
  currency: string;
  explanation: string;
  bestFreight: string;
  freightPrice: string;
  worstFreight: string;
  explanationSteps: {
    title: string;
    explanation: string;
  }[];
  logisticTransportCompanyUuid: string;
}

export type QuotationAIComplementSmartLastMileProps = null;

export interface QuotationAIComplementDataProps {
  alerts?: QuotationAIComplementAlertKeyProps;
  reasonFields?: QuotationAIComplementDataReasonFieldsProps;
  smartLastMile?: QuotationAIComplementSmartLastMileProps;
  smartFirstMile?: QuotationAIComplementSmartFirstMileProps;
}

export interface QuotationProps {
  uuid: string;
  quotationRequest: {
    uuid: string;
    reference: string;
  };
  creatorEmail: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  totalPrice: number;
  totalFreightPrice: number;
  firstMileFreightCost: number;
  lastMileFreightCost: number;
  customsFreightCost: number;
  creditTerm?: string | null;
  financingCost?: string | null;
  discount: string;
  invoiceOption: keyof typeof QuotationInvoiceOptionEnum;
  recurrencyOption: keyof typeof QuotationRecurrencyOptionEnum;
  status: keyof typeof QuotationStatusEnum;
  reference: string;
  deliveryOption: keyof typeof QuotationDeliveryEnum;
  rejectedReason: string;
  creatorUser: number;
  products: QuotationProductProps[];
  buyerProfileUuid: string;
  deliveryAddressUuid: string;
  quotationRequestUuid: string;
  deliveryAddress: AddressProps;
  buyerProfile: ProfileProps;
  orders: {
    uuid: string;
    reference: string;
  }[];
  includeIva: boolean;
  isAutomated: boolean;
  isAiAutomated: boolean;
  expectedDeliveryDays: number;
  dollarExchangeRate: number;
  purchaseOnCredit: boolean;
  paymentFormOption: keyof typeof FinanceProfilePaymentFormsEnum;
  paymentMethodOption: keyof typeof FinanceProfileTransactionPaymentMethodsEnum;
  logisticTransportCompany?: TransportCompanyProps;
  logisticTransportCompanyUuid?: string;
  isSample?: boolean;
  isApproved?: boolean;
  keyAccountManager?: UserProps;
  disableNotifications?: boolean;
  aiComplementData?: QuotationAIComplementDataProps;
  acceptorUser: SupplierOrderCreatorUserProps;
  acceptedOrRejectedFrom: keyof typeof QuotationAcceptedOrRejectedFromEnum;
}

export interface QuotationsListWithPagesProps {
  count: number;
  next?: string;
  previous?: string;
  results?: QuotationProps[];
}

export interface QuotationClassificationProps {
  total: string;
  pending: string;
  created: string;
  active: string;
  pendingApproval: string;
  accepted: string;
  expired: string;
  rejected: string;
}

export interface QuotationSLASProps {
  activeToAcceptedConvertion: number;
  activeToAcceptedConvertionVariation: number;
  activeToAcceptedSla: number;
  activeToAcceptedSlaVariation: number;
  pendingToActiveConvertion: number;
  pendingToActiveConvertionVariation: number;
  pendingToActiveSla: number;
  pendingToActiveSlaVariation: number;
}
